import { useLocalization } from '@lib/localization';
import {
  CheckCircle as CheckCircleIcon, Code as CodeIcon, Error as ErrorIcon, Refresh as RefreshIcon
} from '@mui/icons-material';
import { Box, CardContent, CircularProgress, Grid2, Paper, Typography } from '@mui/material';
import { useGetStatusQuery } from '@store/api/rootEndpoints';
import { useAppDispatch } from '@store/hooks';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { useState } from 'react';
import {
  ActionButton,
  CardBox,
  CardDescription,
  CardHeader,
  CardTitle,
  InfoBox,
  InfoItem,
  JsonDisplay,
  LoadingContainer,
  ProgressContainer,
  SectionContainer,
  ServiceIndicator,
  StatusContainer,
  StatusIcon,
  StatusIconContainer,
  StatusTextContainer,
  StyledLinearProgress,
  SystemContainer,
} from './ServerConfig.styled';

const ServerStatusCard = () => {
  const { data: serverStatus, refetch, isFetching, isLoading } = useGetStatusQuery();
  const [showRawJson, setShowRawJson] = useState(false);
  const dispatch = useAppDispatch();

  const { t, currentLanguage } = useLocalization();

  const refreshServerStatus = async () => {
    await refetch();
    dispatch(showSnackbar({ severity: 'info', message: t('Api_Status_Refetch') }));
  };

  const toggleRawJson = () => {
    setShowRawJson(!showRawJson);
  };

  const calculateMemoryUsage = (): number => {
    if (!serverStatus) return 0;

    const { totalBytes, freeBytes } = serverStatus.system.memory;

    return Math.round(((totalBytes - freeBytes) / totalBytes) * 100);
  };

  /**
   * The two largest units that carry anything, in the reader's language. The server used
   * to send this as English text built from one pair of units, so a process up for three
   * days reported itself in thousands of minutes.
   */
  const formatDuration = (totalSeconds: number): string => {
    const units = [
      { key: 'Common_Day_Count', seconds: 24 * 60 * 60 },
      { key: 'Common_Hour_Count', seconds: 60 * 60 },
      { key: 'Common_Minute_Count', seconds: 60 },
      { key: 'Common_Second_Count', seconds: 1 },
    ];

    const parts: string[] = [];
    let remaining = Math.floor(totalSeconds);

    for (const unit of units) {
      const amount = Math.floor(remaining / unit.seconds);
      remaining %= unit.seconds;

      if (amount > 0) parts.push(t(unit.key, { count: amount }));
      if (parts.length === 2) break;
    }

    return parts.length ? parts.join(', ') : t('Common_Second_Count', { count: 0 });
  };

  return (
    <Paper sx={{ mb: 2 }}>
      <CardHeader>
        <Box>
          <CardTitle variant="h6">{t('ServerConfig_ServerStatus')}</CardTitle>
          <CardDescription variant="body2">{t('ServerConfig_ServerStatus_Description')}</CardDescription>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ActionButton onClick={toggleRawJson} size="small">
            <CodeIcon fontSize="small" />
          </ActionButton>
          <ActionButton onClick={refreshServerStatus} disabled={isFetching} size="small">
            {isFetching ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
          </ActionButton>
        </Box>
      </CardHeader>
      <CardContent style={{ padding: '24px' }}>
        {isLoading && (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        )}
        {!isLoading &&
          serverStatus &&
          (showRawJson ? (
            <JsonDisplay>{JSON.stringify(serverStatus, null, '\t')}</JsonDisplay>
          ) : (
            <>
              <StatusContainer>
                <StatusIconContainer status={serverStatus.status}>
                  <StatusIcon status={serverStatus.status} />
                </StatusIconContainer>
                <Box>
                  <StatusTextContainer>
                    <Typography variant="body1" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                      {serverStatus.status}
                    </Typography>
                    {serverStatus.status === 'ok' ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <ErrorIcon fontSize="small" color="error" />
                    )}
                  </StatusTextContainer>
                  <Typography variant="body2" color="text.secondary">
                    {t('ServerConfig_ServerStatus_ServerTime')}: {new Date(serverStatus.timestamp).toLocaleString(currentLanguage)}
                  </Typography>
                </Box>
              </StatusContainer>

              <SectionContainer>
                <Typography variant="subtitle2" gutterBottom>
                  {t('ServerConfig_ServerStatus_Services')}
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CardBox>
                      <ServiceIndicator connected={serverStatus.database.status === 'connected'} />
                      <Typography variant="body2">{t('ServerConfig_ServerStatus_Database')}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                        {serverStatus.database.status}
                      </Typography>
                    </CardBox>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CardBox>
                      <ServiceIndicator connected={serverStatus.storage.status === 'connected'} />
                      <Typography variant="body2">{t('ServerConfig_ServerStatus_Storage')}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                        {serverStatus.storage.status}
                      </Typography>
                    </CardBox>
                  </Grid2>
                </Grid2>
              </SectionContainer>

              <SectionContainer mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('ServerConfig_ServerStatus_Process')}
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <InfoBox>
                      <Typography variant="caption" color="text.secondary">
                        {t('ServerConfig_ServerStatus_Uptime')}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDuration(serverStatus.process.uptimeSeconds)}
                      </Typography>
                    </InfoBox>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <InfoBox>
                      <Typography variant="caption" color="text.secondary">
                        {t('ServerConfig_ServerStatus_ResponseTime')}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {`${serverStatus.process.responseTimeMs.toFixed(2)} ms`}
                      </Typography>
                    </InfoBox>
                  </Grid2>
                </Grid2>
              </SectionContainer>

              <SectionContainer>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <InfoBox>
                      <Grid2 container spacing={1}>
                        <Grid2 size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('ServerConfig_ServerStatus_RSS')}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {serverStatus.process.memory.rssBytes.formatFileSize(currentLanguage)}
                          </Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('ServerConfig_ServerStatus_HeapTotal')}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {serverStatus.process.memory.heapTotalBytes.formatFileSize(currentLanguage)}
                          </Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('ServerConfig_ServerStatus_HeapUsed')}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {serverStatus.process.memory.heapUsedBytes.formatFileSize(currentLanguage)}
                          </Typography>
                        </Grid2>
                      </Grid2>
                    </InfoBox>
                  </Grid2>
                </Grid2>
              </SectionContainer>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('ServerConfig_ServerStatus_System')}
                </Typography>
                <SystemContainer>
                  <Grid2 container spacing={2}>
                    {/* Hostname */}
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <InfoItem>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('ServerConfig_ServerStatus_Hostname')}
                        </Typography>
                        <Typography variant="body2">{serverStatus.system.hostname}</Typography>
                      </InfoItem>
                    </Grid2>
                    {/* Platform */}
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <InfoItem>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('ServerConfig_ServerStatus_Platform')}
                        </Typography>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {serverStatus.system.os.platform} ({serverStatus.system.arch})
                        </Typography>
                      </InfoItem>
                    </Grid2>
                    {/* System Uptime */}
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <InfoItem>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('ServerConfig_ServerStatus_SystemUptime')}
                        </Typography>
                        <Typography variant="body2">{formatDuration(serverStatus.system.os.uptimeSeconds)}</Typography>
                      </InfoItem>
                    </Grid2>
                    {/* OS Release */}
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <InfoItem>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('ServerConfig_ServerStatus_OSRelease')}
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {serverStatus.system.os.release}
                        </Typography>
                      </InfoItem>
                    </Grid2>
                    {/* CPU */}
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <InfoItem>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('ServerConfig_ServerStatus_CPU')}
                        </Typography>
                        <Typography variant="body2">
                          {`${t('ServerConfig_ServerStatus_Core_Count', { count: serverStatus.system.cpu.cores })} | ${t('ServerConfig_ServerStatus_LoadAvg', [serverStatus.system.cpu.loadAverage.join(', ')])}`}
                        </Typography>
                        <ProgressContainer>
                          <StyledLinearProgress
                            variant="determinate"
                            value={(serverStatus.system.cpu.loadAverage[0] / serverStatus.system.cpu.cores) * 100}
                            color={
                              serverStatus.system.cpu.loadAverage[0] / serverStatus.system.cpu.cores > 0.7
                                ? 'error'
                                : 'primary'
                            }
                          />
                        </ProgressContainer>
                      </InfoItem>
                    </Grid2>
                    {/* RAM */}
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <InfoItem>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('ServerConfig_ServerStatus_RAM')}
                        </Typography>
                        <Typography variant="body2">
                          {t('ServerConfig_ServerStatus_RAM_Values', [
                            serverStatus.system.memory.freeBytes.formatFileSize(currentLanguage),
                            serverStatus.system.memory.totalBytes.formatFileSize(currentLanguage),
                          ])}
                        </Typography>
                        <ProgressContainer>
                          <StyledLinearProgress variant="determinate" value={calculateMemoryUsage()} color="primary" />
                        </ProgressContainer>
                      </InfoItem>
                    </Grid2>
                  </Grid2>
                </SystemContainer>
              </Box>
            </>
          ))
        }
      </CardContent >
    </Paper >
  );
};

export default ServerStatusCard;
